/**
 * Parts of the following code are from the ReactFlow Pro Shapes example.
 * Copyright Webkid GmbH. All rights reserved.
 * Progranimate has been given the license to use this code for educational use under the following
 * conditions:
 *
 * https://www.xyflow.com/terms-of-use Section 2a (as of Friday, December 1)
 * Access to Pro Examples: Pro Examples are advanced examples built on top of the React Flow library.
 * The User may access the source code of all Pro Examples for the term of the Service Agreement.
 * Once the Service Agreement ends, the User will not have access to the source code of the Pro
 * Examples. Webkid may add, update, change or remove Pro Examples during the term of the Service
 * Agreement.The source code of the Pro Examples can be copied, adapted and reused by the User from
 * the start of the Service Agreement for an unlimited amount of time. The source code of the Pro
 * Examples is not allowed for redistribution, repackaging, or for building similar services as
 * React Flow Pro. The Pro Examples are provided on the React Flow Pro website. The Code is embedded
 * in the website and can be copied to the User's environment. Webkid is not liable for the provided
 * source code or any of it's dependencies. Webkid does not provide any guarantee that the code will
 * work in the User's environment.
 */
import React, { ReactNode } from "react";


type ShapeType = "circle" | "round-rect" | "hexagon" | "diamond" | "arrow-rect" | "database" | "triangle" | "parallelogram"
interface props {
  children: ReactNode;
  width: number;
  height: number;
  shapeType: ShapeType;
  color: string;
  selected?: boolean
}
export const Shape: React.FC<props> = ({ shapeType, children, width, height, color, selected=true }) => {
    const shape = useShape({
        type: shapeType,
        width: width,
        height: height,
        color: color,
        selected: false
    });
  return (
    <div style={{ position: "relative" }}>
      <svg style={{ display: "block", overflow: "visible" }} width={width} height={height}>
        {shape}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
};

function useShape({
  type,
  width,
  height,
  color = "#9ca8b3",
  selected,
}: {
  type: ShapeType;
  width: number,
  height: number
  color: string,
  selected: boolean
}) {
  const styles = { fill: color, strokeWidth: selected ? 2 : 0, stroke: "#fff" };

  switch (type) {
    case "circle":
      return <ellipse cx={width / 2} cy={height / 2} rx={width / 2} ry={height / 2} {...styles} />;
    case "round-rect":
      return <rect x={0} y={0} rx={20} width={width} height={height} {...styles} />;
    case "hexagon":
      return (
        <path
          d={`M10,0 L${width - 10},0  L${width},${height / 2} L${width - 10},${height} L10,${height} L0,${
            height / 2
          } z`}
          {...styles}
        />
      );
    case "diamond":
      return (
        <path d={`M0,${height / 2} L${width / 2},0 L${width},${height / 2} L${width / 2},${height} z`} {...styles} />
      );
    case "arrow-rect":
      return (
        <path
          d={`M0,0 L${width - 10},0  L${width},${height / 2} L${width - 10},${height} L0,${height} z`}
          {...styles}
        />
      );
    case "database":
      return (
        <path
          d={`M0,${height * 0.125}  L 0,${height - height * 0.125} A ${width / 2} ${height * 0.125} 0 1 0 ${width} ${
            height - height * 0.125
          } L ${width},${height * 0.125} A ${width / 2} ${height * 0.125} 0 1 1 0 ${height * 0.125} A ${width / 2} ${
            height * 0.125
          } 0 1 1 ${width} ${height * 0.125} A ${width / 2} ${height * 0.125} 0 1 1 0 ${height * 0.125} z`}
          {...styles}
          strokeWidth={selected ? styles.strokeWidth : 1}
        />
      );
    case "triangle":
      return <path d={`M0,${height} L${width / 2},0 L${width},${height} z`} {...styles} />;
    case "parallelogram":
      return (
        <path d={`M0,${height} L${width * 0.25},0 L${width},0 L${width - width * 0.25},${height} z`} {...styles} />
      );
    default:
      return null;
  }
}
